import { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    LineChart,
    Line,
    Cell,
} from 'recharts';
import {
    Star,
    MessageSquare,
    TrendingUp,
    TrendingDown,
    Filter,
    CalendarDays,
    X,
} from 'lucide-react';
import api from '@/lib/api';
import { cn } from '@/lib/utils';

const RATING_COLORS = {
    1: '#ef4444',
    2: '#f97316',
    3: '#eab308',
    4: '#84cc16',
    5: '#22c55e',
};

const FACE_LABELS = {
    1: 'Muy insatisfecho',
    2: 'Insatisfecho',
    3: 'Neutral',
    4: 'Satisfecho',
    5: 'Muy satisfecho',
};

export default function OwnerSatisfaction() {
    const [stats, setStats] = useState(null);
    const [classes, setClasses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({
        date_from: '',
        date_to: '',
        class_id: '',
        rating: '',
    });
    const [showFilters, setShowFilters] = useState(false);

    const fetchStats = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (filters.date_from) params.append('date_from', filters.date_from);
            if (filters.date_to) params.append('date_to', filters.date_to);
            if (filters.class_id) params.append('class_id', filters.class_id);
            if (filters.rating) params.append('rating', filters.rating);

            const res = await api.get(`/owner/satisfaction-stats?${params.toString()}`);
            setStats(res.data.data);
        } catch (err) {
            console.error(err);
        } finally { setLoading(false); }
    }, [filters]);

    useEffect(() => {
        fetchStats();
    }, [fetchStats]);

    useEffect(() => {
        const fetchClasses = async () => {
            try {
                const res = await api.get('/owner/classes?per_page=100');
                setClasses(res.data.data || []);
            } catch {}
        };
        fetchClasses();
    }, []);

    const applyFilters = () => {
        fetchStats();
        setShowFilters(false);
    };

    const clearFilters = () => {
        setFilters({ date_from: '', date_to: '', class_id: '', rating: '' });
    };

    const hasActiveFilters = filters.date_from || filters.date_to || filters.class_id || filters.rating;

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <h1 className="text-2xl font-bold uppercase tracking-wide text-white">
                    Satisfacción de Alumnos
                </h1>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowFilters(!showFilters)}
                    className={cn(
                        "border-hairline-dark text-white hover:bg-white/5",
                        hasActiveFilters && "bg-white/10"
                    )}
                >
                    <Filter className="h-4 w-4 mr-2" />
                    Filtros
                    {hasActiveFilters && <span className="ml-1.5 h-2 w-2 rounded-full bg-white" />}
                </Button>
            </div>

            {showFilters && (
                <Card className="bg-[#0a0a0a] border-hairline-dark">
                    <CardContent className="py-4">
                        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                            <div>
                                <label className="text-[10px] uppercase tracking-widest text-white/40 mb-1.5 block">
                                    Desde
                                </label>
                                <input
                                    type="date"
                                    value={filters.date_from}
                                    onChange={e => setFilters(f => ({ ...f, date_from: e.target.value }))}
                                    className="w-full h-9 px-3 text-sm text-white bg-black border border-hairline-dark rounded focus:outline-none focus:ring-1 focus:ring-white"
                                />
                            </div>
                            <div>
                                <label className="text-[10px] uppercase tracking-widest text-white/40 mb-1.5 block">
                                    Hasta
                                </label>
                                <input
                                    type="date"
                                    value={filters.date_to}
                                    onChange={e => setFilters(f => ({ ...f, date_to: e.target.value }))}
                                    className="w-full h-9 px-3 text-sm text-white bg-black border border-hairline-dark rounded focus:outline-none focus:ring-1 focus:ring-white"
                                />
                            </div>
                            <div>
                                <label className="text-[10px] uppercase tracking-widest text-white/40 mb-1.5 block">
                                    Clase
                                </label>
                                <select
                                    value={filters.class_id}
                                    onChange={e => setFilters(f => ({ ...f, class_id: e.target.value }))}
                                    className="w-full h-9 px-3 text-sm text-white bg-black border border-hairline-dark rounded focus:outline-none focus:ring-1 focus:ring-white appearance-none cursor-pointer"
                                >
                                    <option value="">Todas</option>
                                    {classes.map(c => (
                                        <option key={c.id} value={c.id}>{c.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="text-[10px] uppercase tracking-widest text-white/40 mb-1.5 block">
                                    Calificación
                                </label>
                                <select
                                    value={filters.rating}
                                    onChange={e => setFilters(f => ({ ...f, rating: e.target.value }))}
                                    className="w-full h-9 px-3 text-sm text-white bg-black border border-hairline-dark rounded focus:outline-none focus:ring-1 focus:ring-white appearance-none cursor-pointer"
                                >
                                    <option value="">Todas</option>
                                    {[1,2,3,4,5].map(r => (
                                        <option key={r} value={r}>{r} — {FACE_LABELS[r]}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="flex gap-2 mt-4">
                            <Button size="sm" onClick={applyFilters} className="btn-ghost text-xs py-2 px-4">
                                Aplicar
                            </Button>
                            {hasActiveFilters && (
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={clearFilters}
                                    className="text-white/60 hover:text-white text-xs"
                                >
                                    <X className="h-3 w-3 mr-1" />
                                    Limpiar
                                </Button>
                            )}
                        </div>
                    </CardContent>
                </Card>
            )}

            {loading && !stats ? (
                <p className="text-white/60 text-center py-8">Cargando...</p>
            ) : !stats ? (
                <p className="text-white/60 text-center py-8">No hay datos disponibles.</p>
            ) : (
                <>
                    {/* Summary cards */}
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                        <Card className="bg-[#0a0a0a] border-hairline-dark">
                            <CardHeader className="flex flex-row items-center justify-between pb-2">
                                <CardTitle className="text-sm font-medium text-white/60 uppercase tracking-wider">
                                    Promedio general
                                </CardTitle>
                                <Star className="h-5 w-5 text-white/60" />
                            </CardHeader>
                            <CardContent>
                                <div className="text-3xl font-bold text-white">
                                    {stats.average_rating > 0 ? stats.average_rating.toFixed(1) : '-'}
                                    <span className="text-sm font-normal text-white/40 ml-1">/ 5</span>
                                </div>
                            </CardContent>
                        </Card>
                        <Card className="bg-[#0a0a0a] border-hairline-dark">
                            <CardHeader className="flex flex-row items-center justify-between pb-2">
                                <CardTitle className="text-sm font-medium text-white/60 uppercase tracking-wider">
                                    Total respuestas
                                </CardTitle>
                                <MessageSquare className="h-5 w-5 text-white/60" />
                            </CardHeader>
                            <CardContent>
                                <div className="text-3xl font-bold text-white">{stats.total_responses}</div>
                            </CardContent>
                        </Card>
                        <Card className="bg-[#0a0a0a] border-hairline-dark">
                            <CardHeader className="flex flex-row items-center justify-between pb-2">
                                <CardTitle className="text-sm font-medium text-white/60 uppercase tracking-wider">
                                    Mejor clase
                                </CardTitle>
                                <TrendingUp className="h-5 w-5 text-green-400" />
                            </CardHeader>
                            <CardContent>
                                <div className="text-lg font-bold text-white truncate">
                                    {stats.highest_rated?.[0]?.name || '-'}
                                </div>
                                {stats.highest_rated?.[0] && (
                                    <p className="text-xs text-white/60">
                                        {stats.highest_rated[0].average.toFixed(1)} / 5
                                    </p>
                                )}
                            </CardContent>
                        </Card>
                        <Card className="bg-[#0a0a0a] border-hairline-dark">
                            <CardHeader className="flex flex-row items-center justify-between pb-2">
                                <CardTitle className="text-sm font-medium text-white/60 uppercase tracking-wider">
                                    Clase a mejorar
                                </CardTitle>
                                <TrendingDown className="h-5 w-5 text-orange-400" />
                            </CardHeader>
                            <CardContent>
                                <div className="text-lg font-bold text-white truncate">
                                    {stats.lowest_rated?.[0]?.name || '-'}
                                </div>
                                {stats.lowest_rated?.[0] && (
                                    <p className="text-xs text-white/60">
                                        {stats.lowest_rated[0].average.toFixed(1)} / 5
                                    </p>
                                )}
                            </CardContent>
                        </Card>
                    </div>

                    {/* Charts row */}
                    <div className="grid gap-4 lg:grid-cols-2">
                        {/* Distribution bar chart */}
                        <Card className="bg-[#0a0a0a] border-hairline-dark">
                            <CardHeader className="pb-2">
                                <CardTitle className="text-sm font-semibold uppercase tracking-wide text-white">
                                    Distribución de calificaciones
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="h-64">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={stats.distribution} barCategoryGap="20%">
                                            <CartesianGrid strokeDasharray="3 3" stroke="#3a3a3f" />
                                            <XAxis
                                                dataKey="rating"
                                                tick={{ fill: '#ffffff', fontSize: 12 }}
                                                axisLine={{ stroke: '#3a3a3f' }}
                                            />
                                            <YAxis
                                                tick={{ fill: '#ffffff', fontSize: 12 }}
                                                axisLine={{ stroke: '#3a3a3f' }}
                                                allowDecimals={false}
                                            />
                                            <Tooltip
                                                contentStyle={{
                                                    backgroundColor: '#0a0a0a',
                                                    border: '1px solid #3a3a3f',
                                                    borderRadius: '4px',
                                                    color: '#ffffff',
                                                }}
                                                formatter={(value) => [value, 'Respuestas']}
                                                labelFormatter={(label) => `${label} — ${FACE_LABELS[label]}`}
                                            />
                                            <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                                                {stats.distribution.map((entry) => (
                                                    <Cell key={entry.rating} fill={RATING_COLORS[entry.rating]} />
                                                ))}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Trend line chart */}
                        <Card className="bg-[#0a0a0a] border-hairline-dark">
                            <CardHeader className="pb-2">
                                <CardTitle className="text-sm font-semibold uppercase tracking-wide text-white">
                                    Tendencia de satisfacción
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="h-64">
                                    {stats.trend.length === 0 ? (
                                        <div className="flex items-center justify-center h-full text-white/40 text-sm">
                                            No hay datos suficientes para mostrar la tendencia.
                                        </div>
                                    ) : (
                                        <ResponsiveContainer width="100%" height="100%">
                                            <LineChart data={stats.trend}>
                                                <CartesianGrid strokeDasharray="3 3" stroke="#3a3a3f" />
                                                <XAxis
                                                    dataKey="week"
                                                    tick={{ fill: '#ffffff', fontSize: 11 }}
                                                    axisLine={{ stroke: '#3a3a3f' }}
                                                    angle={-30}
                                                    textAnchor="end"
                                                    height={50}
                                                />
                                                <YAxis
                                                    domain={[1, 5]}
                                                    tick={{ fill: '#ffffff', fontSize: 12 }}
                                                    axisLine={{ stroke: '#3a3a3f' }}
                                                />
                                                <Tooltip
                                                    contentStyle={{
                                                        backgroundColor: '#0a0a0a',
                                                        border: '1px solid #3a3a3f',
                                                        borderRadius: '4px',
                                                        color: '#ffffff',
                                                    }}
                                                    formatter={(value) => [Number(value).toFixed(2), 'Promedio']}
                                                    labelFormatter={(label) => `Semana ${label}`}
                                                />
                                                <Line
                                                    type="monotone"
                                                    dataKey="average"
                                                    stroke="#ffffff"
                                                    strokeWidth={2}
                                                    dot={{ fill: '#ffffff', r: 3 }}
                                                    activeDot={{ r: 5 }}
                                                />
                                            </LineChart>
                                        </ResponsiveContainer>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Class averages table */}
                    <Card className="bg-[#0a0a0a] border-hairline-dark">
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm font-semibold uppercase tracking-wide text-white">
                                Promedio por clase
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {stats.class_averages.length === 0 ? (
                                <p className="text-white/40 text-sm text-center py-6">No hay calificaciones por clase.</p>
                            ) : (
                                <div className="space-y-3">
                                    {stats.class_averages.map(c => (
                                        <div key={c.id} className="flex items-center gap-4">
                                            <div className="w-32 sm:w-48 shrink-0">
                                                <p className="text-sm font-medium text-white truncate">{c.name}</p>
                                            </div>
                                            <div className="flex-1">
                                                <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                                                    <div
                                                        className="h-full rounded-full transition-all"
                                                        style={{
                                                            width: `${(c.average / 5) * 100}%`,
                                                            backgroundColor: RATING_COLORS[Math.round(c.average)] || '#ffffff',
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                            <div className="w-16 text-right shrink-0">
                                                <span className="text-sm font-bold text-white">{c.average.toFixed(1)}</span>
                                                <span className="text-xs text-white/40 ml-0.5">/5</span>
                                            </div>
                                            <div className="w-14 text-right shrink-0">
                                                <span className="text-xs text-white/40">{c.count} voto{c.count !== 1 ? 's' : ''}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Recent comments */}
                    <Card className="bg-[#0a0a0a] border-hairline-dark">
                        <CardHeader className="flex flex-row items-center gap-3 pb-2">
                            <div className="h-8 w-8 bg-white/10 rounded-full flex items-center justify-center">
                                <MessageSquare className="h-4 w-4 text-white" />
                            </div>
                            <CardTitle className="text-sm font-semibold uppercase tracking-wide text-white">
                                Comentarios recientes
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {stats.recent_comments.length === 0 ? (
                                <p className="text-white/40 text-sm text-center py-6">No hay comentarios aún.</p>
                            ) : (
                                <div className="space-y-3 max-h-[400px] overflow-y-auto pr-1">
                                    {stats.recent_comments.map(comment => {
                                        const color = RATING_COLORS[comment.rating];
                                        return (
                                            <div key={comment.id} className="p-3 rounded bg-black border border-hairline-dark">
                                                <div className="flex items-center justify-between mb-2">
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-lg leading-none">
                                                            {['😠','🙁','😐','🙂','😍'][comment.rating - 1]}
                                                        </span>
                                                        <span className="text-xs font-medium text-white/80">{comment.customer_name}</span>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <Badge
                                                            className="text-[10px] border-none"
                                                            style={{ backgroundColor: color + '33', color }}
                                                        >
                                                            {comment.rating} / 5
                                                        </Badge>
                                                        <span className="text-[10px] text-white/40">{comment.created_at}</span>
                                                    </div>
                                                </div>
                                                <p className="text-sm text-white/70">{comment.comment}</p>
                                                <p className="text-xs text-white/40 mt-1">{comment.class_name}</p>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </>
            )}
        </div>
    );
}
